import React from "react";
import { Avatar, Checkbox, Typography, Box } from "@mui/material";
// import TurnRightIcon from "@mui/icons-material/TurnRight";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import "react-quill/dist/quill.snow.css";
import { IMail, IMailBoxAuthData } from "src/types/mail";
import { useAppDispatch } from "src/redux/store";
import { formatTimeStampDate, getInitials } from "src/constants/common";
import { getSessionToken } from "@descope/react-sdk";
import {
  setCurrentEmail,
  UpdateEmailStatus,
} from "src/redux/features/mailBox/mailbox-slice";
import { ReactComponent as Download } from "../../assets/images/images-svg/Download.svg";
import { setResponseValue } from "src/redux/features/api-response/api-response";

const EmailContent: React.FC<{
  mailData: IMail | null;
  mailBoxAuthData: IMailBoxAuthData | null;
}> = ({ mailData, mailBoxAuthData }) => {
  const dispatch = useAppDispatch();
  const handleDownloadEmailAttachment = async (
    messageId: string,
    attachmentId: string,
    fileName: string
  ) => {
    const token = getSessionToken();
    const BASE_URL = process.env.REACT_APP_BACKEND_SERVER_BASE_URL;

    try {
      // Create URL with query parameters
      const url = `${BASE_URL}/mailbox/attachment/download?email=${mailBoxAuthData?.email}&messageId=${messageId}&attachmentId=${attachmentId}`;

      // Use fetch with blob response
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Check if response is JSON (error) or blob (success)
      const contentType = response.headers.get("content-type");

      if (
        contentType &&
        contentType.includes("application/json") &&
        !response.ok
      ) {
        // This is likely an error response, parse it to get the error message
        const errorData = await response.json();

        // Extract the backend error message
        const backendMessage =
          errorData.error || errorData.message || "Unknown error occurred";

        // Throw an error with the backend message
        throw new Error(backendMessage);
      }

      if (!response.ok) {
        throw new Error(`Download failed with status: ${response.status}`);
      }

      // Convert response to blob
      const blob = await response.blob();

      // Create object URL
      const objectUrl = URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;

      // Append to document, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      URL.revokeObjectURL(objectUrl);
      dispatch(setResponseValue({ name: "success", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: `Attachment downloaded successfully`,
        })
      );
    } catch (error: any) {
      console.log("Error downloading attachment:", error);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(setResponseValue({ name: "message", value: error?.message }));
    }
  };
  return (
    <div>
      <div className="email-header">
        <div className="senderEmail">
          <Avatar sx={{ bgcolor: "#096dd9", color: "white" }}>
            {mailData?.from[0]?.name
              ? getInitials(mailData?.from[0]?.name)
              : "UK"}
          </Avatar>
          <div>
            <Typography className="senderEmail">
              {mailData?.from[0].email}
            </Typography>
            <div className="email-to">
              <Typography className="to-text">to</Typography>
              {mailData?.to.map((item, index) => (
                <Typography className="to-text">
                  {item.email === mailBoxAuthData?.email ? "me" : item.email}
                  {index === mailData?.to.length - 1 ? "" : ","}
                </Typography>
              ))}
            </div>
          </div>
        </div>

        <div className="email-actions">
          <Typography className="email-date">
            {formatTimeStampDate(mailData?.date!, "DetailsView")}
          </Typography>
          {mailBoxAuthData?.provider !== "microsoft" && (
            <Checkbox
              onClick={() => {
                if (!mailBoxAuthData?.email || !mailData?.id) {
                  return;
                }
                dispatch(
                  setCurrentEmail({
                    ...mailData,
                    starred: !mailData?.starred,
                  })
                );
                dispatch(
                  UpdateEmailStatus({
                    email: mailBoxAuthData?.email,
                    mailId: mailData?.id,
                    starred: !mailData?.starred,
                  })
                );
              }}
              checked={mailData?.starred}
              icon={<StarBorderIcon className="icon" />} // Empty star
              checkedIcon={<StarIcon className="fav-filled" />} // Filled star
            />
          )}

          {/* <TurnLeftIcon className="icon" /> */}
          {/* <DeleteOutlinedIcon
              className="icon"
              onClick={() => {
                if (!mailBoxAuthData?.email) {
                  return;
                }
                dispatch(
                  DeleteMultipleEmails({
                    userEmail: mailBoxAuthData?.email,
                    emailIds: [mailData?.id!],
                  })
                );
                navigate("/mailbox");
                dispatch(setCurrentEmail(null));
              }}
            /> */}
        </div>
      </div>
      <div
        dangerouslySetInnerHTML={{
          __html: mailData?.body ? mailData?.body : "",
        }}
        className="email-body"
      />
      {mailData?.attachments && mailData?.attachments?.length > 0 && (
        <div className="emailAttachments">
          <Typography>
            <b>{mailData?.attachments.length} Attachments</b>
          </Typography>
          <Box className="attachmentBox">
            {mailData?.attachments.map((item) => (
              <div className="attachment">
                <Box className="attachment__box"></Box>
                <Box className="attachment__footer">
                  <Typography className="attachment__name">
                    {item.filename}
                  </Typography>
                  <Download
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownloadEmailAttachment(
                        mailData.id,
                        item.id,
                        item.filename
                      );
                    }}
                  />
                </Box>
              </div>
            ))}
          </Box>
        </div>
      )}
    </div>
  );
};

export default EmailContent;
